import { FilePurpose, languageDisplayCode, LanguagesEnum } from '@/api/enums';
import { equal } from '@/api/helpers';
import { Account } from '@/client/accounts';
import {
  ContentSeries,
  Course,
  CourseFormState,
  MaterialType,
  ResourceType,
} from '@/client/courses';
import { File } from '@/client/files';
import { PlannerType } from '@/client/planner/types';
import { Tag } from '@/client/tags';
import { Actions, Subjects, SystemRoles } from '@/client/users';
import {
  LoadingStateType,
  RedirectPaths,
  RedirectPathsEnum,
} from '@/common/constants';
import { COURSE_THUMBNAIL_ACCEPTED_FORMATS } from '@/common/constants/files';
import { availableLanguages } from '@/common/constants/languages';
import { createCourseSchema } from '@/components/courses/forms/validations';
import {
  FormikDropdown,
  FormikInput,
  FormikMultiSelect,
  FormikRichTextArea,
  FormikSwitch,
} from '@/components/form';
import { FormikInputNumber } from '@/components/form/FormikInputNumber';
import { AccountSelectInput } from '@/components/form/selectors';
import { TagsSelector } from '@/components/form/selectors/TagsSelector';
import { useAppSelector } from '@/hooks/store';
import { usePermission } from '@/hooks/usePermission';
import { selectCurrentAccount } from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton } from '@/ui/buttons';
import { AppCalendar } from '@/ui/calendar';
import { CheckboxesContainer } from '@/ui/checkboxes-container';
import { AppFormAccordion } from '@/ui/form-accordion';
import { AppIconTooltip } from '@/ui/icon-tooltip';
import { ImageUpload } from '@/ui/image-upload';
import { FlexContainer, FormContainer } from '@/ui/styled-ui';
import { TabSwitchButtons } from '@/ui/tab-switch-buttons';
import {
  courseLanguagesOptions,
  isDemoRestrictedMode,
  sortAlphabetically,
  standardCourseLabelOptions,
} from '@/utils/helpers';
import classNames from 'classnames';
import { Field, Form, Formik, FormikErrors, FormikValues } from 'formik';
import { isEqual, omit } from 'lodash';
import moment from 'moment';
import { DropdownChangeEvent } from 'primereact/dropdown';
import { InputNumberChangeEvent } from 'primereact/inputnumber';
import { InputSwitchChangeEvent } from 'primereact/inputswitch';
import { MultiSelectChangeEvent } from 'primereact/multiselect';
import { Tooltip } from 'primereact/tooltip';
import { FormEvent } from 'primereact/ts-helpers';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ContentTimeline } from '../ContentTimeline';
import { UnsavedChangesModal } from '../modals/UnsavedChangesModal';

const StyledWrapper = styled(FlexContainer)`
  h3 {
    font-size: var(--heading-medium-font-size);
    line-height: var(--heading-medium-line-height);
  }

  h4 {
    margin-top: var(--semi-big-padding);
    font-size: var(--heading-small-font-size);
    line-height: var(--heading-small-line-height);
  }
`;

type ContentFormProps = {
  initialValues: CourseFormState;
  state?: LoadingStateType;
  onSubmit: (data: FormikValues) => void;
  courseThumbnailData?: File;
  customAccountData?: Account;
  isCreate?: boolean;
  selfHosted?: boolean;
  defaultLanguage?: string;
  onUnsavedChangesCheck?: (hasChanges: boolean) => void;
  submitFormRef?: React.MutableRefObject<(() => Promise<void>) | null>;
  onErrorsChange?: (errors: FormikErrors<CourseFormState>) => void;
  course?: Course;
};

export const ContentForm: React.FC<ContentFormProps> = ({
  initialValues,
  state,
  onSubmit,
  courseThumbnailData,
  customAccountData,
  isCreate = false,
  defaultLanguage,
  onUnsavedChangesCheck,
  submitFormRef,
  onErrorsChange,
  course,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const currentUser = useAppSelector(selectCurrentUser);
  const isDemoAccount = isDemoRestrictedMode(account, currentUser);
  const isSystem = account?.isSystem;
  const retired = !!initialValues.retired;
  const { cannot } = usePermission();
  const [showForm, setShowForm] = useState(false);

  const disabled: boolean =
    cannot(Actions.CREATE, Subjects.COURSES) ||
    (!!(initialValues.id && initialValues?.customAccount !== account?.id) &&
      !account?.isSystem);

  const adminPlatformLanguage = availableLanguages.includes(
    currentUser?.language as LanguagesEnum,
  )
    ? (currentUser?.language as LanguagesEnum)
    : LanguagesEnum.EN;

  const [selectedTab, setSelectedTab] = useState(
    languageDisplayCode[defaultLanguage || adminPlatformLanguage],
  );

  const [selectedCustomAccount, setSelectedCustomAccount] =
    useState<Account | null>(null);
  const [validOptions, setValidOptions] = useState([] as string[]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState<boolean>(false);
  const [currentFormValues, setCurrentFormValues] =
    useState<CourseFormState>(initialValues);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);

  const updateErrors = (errors: any) => {
    if (onErrorsChange) {
      onErrorsChange(errors);
    }
  };

  useEffect(() => {
    if (customAccountData) {
      setSelectedCustomAccount(customAccountData);
    }
  }, [customAccountData]);

  useEffect(() => {
    setValidOptions(checkValidOptions(initialValues));
  }, [initialValues]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowForm(true);
    }, 100); // Small delay to ensure all checks are complete

    return () => clearTimeout(timer);
  }, [disabled, retired, account]);

  const checkValidOptions = (values: FormikValues) => {
    return availableLanguages
      .filter((languageCode) => {
        if (values.courseTranslations[languageCode]) {
          return !!values.courseTranslations[languageCode].name;
        }
      })
      .map((languageCode) => languageDisplayCode[languageCode]);
  };

  const showDescription = (
    values: FormikValues,
    languageCode: string,
  ): boolean => {
    if (
      values.type === MaterialType.COURSE &&
      values?.customAccount === account?.id
    )
      return true;
    if (values.courseTranslations && !isSystem && !isCreate) {
      return !!values.courseTranslations[languageCode]?.description;
    }
    return true;
  };

  const hasFormChanges = (
    current: CourseFormState,
    initial: CourseFormState,
  ): boolean => {
    // Since quiz and presentation are handled in separate unsaved changes modals, we can strip them here for better accuracy
    const currentWithoutTranslations = {
      ...current,
      courseContents: current.courseContents?.map((content) =>
        omit(content, ['quiz', 'presentation', 'resource', 'id']),
      ),
    };
    const initialWithoutTranslations = {
      ...initial,
      courseContents: initial.courseContents?.map((content) =>
        omit(content, ['quiz', 'presentation', 'resource', 'id']),
      ),
    };

    return !isEqual(currentWithoutTranslations, initialWithoutTranslations);
  };

  const [pendingUrl, setPendingUrl] = useState<string | null>(null);
  const pageLeaveHandler = async (values: CourseFormState, url?: string) => {
    if (hasFormChanges(values, initialValues)) {
      setShowUnsavedChangesModal(true);
      setPendingUrl(url || null);
    } else {
      navigate(url || RedirectPaths[RedirectPathsEnum.COURSES]());
    }
  };

  useEffect(() => {
    const shouldBlock = hasFormChanges(currentFormValues, initialValues);

    if (onUnsavedChangesCheck) {
      onUnsavedChangesCheck(shouldBlock);
    }
  }, [currentFormValues, initialValues, onUnsavedChangesCheck]);

  const courseLanguagesOptionTemplate = (option: any) => {
    return <span>{option.name}</span>;
  };

  const handleChangeCourseLanguages = (
    event: DropdownChangeEvent,
    _values: CourseFormState,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    setFieldValue('courseLanguages', event.value);
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={createCourseSchema(validOptions, t)}
        onSubmit={onSubmit}
        validateOnChange
        validateOnMount
      >
        {({ errors, setFieldValue, values, validateForm, submitForm }) => {
          setCurrentFormValues(values);
          updateErrors(errors);

          if (submitFormRef) {
            submitFormRef.current = submitForm;
          }

          return (
            showForm && (
              <FormContainer width={100}>
                <StyledWrapper direction="column">
                  {!disabled && !initialValues.retired && (
                    <FlexContainer
                      justify="space-between"
                      align="center"
                      className="mb-3"
                    >
                      <h3 className="m-0 flex-shrink-0">
                        {isCreate ? t('content.new') : t('content.update')}
                      </h3>
                      <FlexContainer justify="flex-end">
                        <AppButton
                          severity="secondary"
                          type="outlined"
                          label={t('button.cancel')}
                          className="mr-3"
                          state={state}
                          onClick={async () => {
                            const validationErrors = await validateForm();
                            updateErrors(validationErrors);
                            if (
                              Object.keys(validationErrors).length > 0 &&
                              hasFormChanges(values, initialValues)
                            ) {
                              setShowUnsavedChangesModal(true);
                              setPendingUrl(null);
                            } else {
                              await pageLeaveHandler(values);
                            }
                          }}
                        />
                        <div className="create">
                          <AppButton
                            onClick={() => {
                              if (isDemoAccount) return;
                              onSubmit(values);
                            }}
                            severity="success"
                            label={
                              isCreate ? t('button.create') : t('button.save')
                            }
                            isSubmit
                            state={state}
                            isDisabled={
                              !!Object.keys(errors).length || isSubmitDisabled
                            }
                          />
                        </div>
                        {!!Object.keys(errors).length && (
                          <Tooltip
                            position="left"
                            target=".create"
                            content={t('generic.requiredFields')}
                          />
                        )}
                      </FlexContainer>
                    </FlexContainer>
                  )}
                  <Form className="w-full" autoComplete="off">
                    <FlexContainer
                      gap={24}
                      width="100%"
                      justify="space-between"
                      align="flex-start"
                    >
                      <div className="w-full">
                        <h4 className="mt-0">{t('content.settings')}</h4>

                        <div className="field">
                          <FlexContainer justify="flex-start" className="mb-2">
                            <label>{t('content.platform.languages')}</label>
                            <AppIconTooltip
                              target="platform-languages-info-icon"
                              content={t('content.platform.languages.tooltip')}
                            />
                          </FlexContainer>
                          <FlexContainer
                            className="mb-4"
                            justify="flex-start"
                            id="course-translations-tabs"
                          >
                            <TabSwitchButtons
                              options={availableLanguages.map(
                                (languageCode) =>
                                  languageDisplayCode[languageCode],
                              )}
                              requiredOptions={[]}
                              validOptions={validOptions}
                              onToggle={(option) =>
                                setSelectedTab(option as string)
                              }
                              defaultSelected={selectedTab}
                            />
                          </FlexContainer>
                        </div>
                        {availableLanguages.map(
                          (languageCode) =>
                            selectedTab ===
                              languageDisplayCode[languageCode] && (
                              <div key={languageCode} className="w-full">
                                <div className="field mb-4">
                                  <Field
                                    disabled={disabled || retired}
                                    id={`name-${languageCode}`}
                                    label={t('generic.name')}
                                    name={`courseTranslations.${languageCode}.name`}
                                    className="w-full"
                                    component={FormikInput}
                                    placeholder={t('content.name')}
                                    additionOnBlur={() => {
                                      setValidOptions(
                                        checkValidOptions(values),
                                      );
                                      setTimeout(async () => {
                                        await validateForm();
                                      }, 0);
                                    }}
                                  />
                                </div>
                                {showDescription(values, languageCode) && (
                                  <div className="field mb-4">
                                    <Field
                                      readOnly={disabled || retired}
                                      id={`description-${languageCode}`}
                                      label={t('generic.description')}
                                      name={`courseTranslations.${languageCode}.description`}
                                      className="w-full"
                                      placeholder={t(
                                        'content.description.placeholder',
                                      )}
                                      component={FormikRichTextArea}
                                    />
                                  </div>
                                )}
                              </div>
                            ),
                        )}
                        {[
                          SystemRoles.SUPER_ADMIN,
                          SystemRoles.DEVELOPER,
                        ].includes(currentUser?.role.code as SystemRoles) &&
                          !course?.account && (
                            <>
                              <div className="field w-full mb-4">
                                <Field
                                  disabled={disabled || retired}
                                  id="content-series"
                                  name="contentSeries"
                                  className="w-full"
                                  label={t('content.series')}
                                  component={FormikDropdown}
                                  placeholder={t('content.series.choose')}
                                  options={Object.values(ContentSeries).map(
                                    (series) => ({
                                      label: t(`content.series.${series}`),
                                      value: series,
                                    }),
                                  )}
                                  showClear
                                />
                              </div>

                              <div className="field w-full mb-4">
                                <TagsSelector
                                  label={t('content.tags')}
                                  selectedTags={values.tags || []}
                                  disabled={disabled || retired}
                                  onAddTags={(tags: Tag[]) => {
                                    setFieldValue('tags', tags);
                                  }}
                                  onRemoveTag={(tagId: string) => {
                                    const currentTags = values.tags || [];
                                    setFieldValue(
                                      'tags',
                                      currentTags.filter(
                                        (t: { id: string }) => t.id !== tagId,
                                      ),
                                    );
                                  }}
                                />
                              </div>
                            </>
                          )}
                        <div className="field w-full mb-4">
                          <ImageUpload
                            label={t('generic.image')}
                            accept={COURSE_THUMBNAIL_ACCEPTED_FORMATS.join(',')}
                            onFileUpload={(file: File | null) =>
                              setFieldValue('courseThumbnail', file?.id)
                            }
                            purpose={FilePurpose.THUMBNAIL}
                            preselectedFile={courseThumbnailData}
                            setIsSubmitDisabled={setIsSubmitDisabled}
                            isDisabled={disabled || retired}
                            required={!!values.releaseDate}
                            hasError={!!errors.courseThumbnail}
                          />
                          {!!errors.courseThumbnail && (
                            <small className="p-error">
                              {errors.courseThumbnail}
                            </small>
                          )}
                        </div>
                        {!disabled && (
                          <AppFormAccordion
                            label={
                              account?.isSystem
                                ? t('content.accordion.header')
                                : t('content.accordion.header.short')
                            }
                          >
                            <div className="field mb-4">
                              <label htmlFor="course-created-at">
                                {t('generic.creation')}
                              </label>
                              <AppCalendar
                                disabled={retired}
                                id="course-created-at"
                                name="courseCreatedAt"
                                className="w-full"
                                showIcon
                                iconPos="left"
                                placeholder={t('generic.select')}
                                maxDate={values.courseLastUpdatedAt}
                                value={values.courseCreatedAt}
                                onChange={(event: FormEvent) =>
                                  setFieldValue('courseCreatedAt', event.value)
                                }
                                showButtonBar
                                onClearButtonClick={() => {
                                  setFieldValue('courseCreatedAt', undefined);
                                }}
                                readOnlyInput
                              />
                            </div>
                            <div className="field mb-4">
                              <label htmlFor="course-last-updated-at">
                                {t('generic.last.updated')}
                              </label>
                              <AppCalendar
                                disabled={retired}
                                id="course-last-updated-at"
                                name="courseLastUpdatedAt"
                                className="w-full"
                                showIcon
                                iconPos="left"
                                placeholder={t('generic.select')}
                                minDate={values.courseCreatedAt}
                                value={values.courseLastUpdatedAt}
                                onChange={(event: FormEvent) =>
                                  setFieldValue(
                                    'courseLastUpdatedAt',
                                    event.value,
                                  )
                                }
                                showButtonBar
                                onClearButtonClick={() => {
                                  setFieldValue(
                                    'courseLastUpdatedAt',
                                    undefined,
                                  );
                                }}
                                readOnlyInput
                              />
                            </div>

                            <FlexContainer justify="flex-start">
                              {account?.isSystem && (
                                <div
                                  className="field-checkbox mb-4 mr-4"
                                  style={{
                                    width: account?.isSystem ? '240px' : 'auto',
                                  }}
                                >
                                  <Field
                                    disabled={
                                      retired ||
                                      values.courseContents?.some(
                                        (content) =>
                                          content?.resource?.type ===
                                          ResourceType.SCORM,
                                      )
                                    }
                                    inputId="isCloneable"
                                    name="isCloneable"
                                    label={t('content.enableCloning')}
                                    value={!!values?.isCloneable}
                                    onChange={async (
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      setFieldValue(
                                        'isCloneable',
                                        e.target.value,
                                      );
                                      setTimeout(async () => {
                                        await validateForm();
                                      }, 0);
                                    }}
                                    component={FormikSwitch}
                                  />
                                </div>
                              )}

                              {account?.isSystem && (
                                <div
                                  className="field-checkbox mb-4 mr-4"
                                  style={{ width: '240px' }}
                                >
                                  <Field
                                    disabled={
                                      retired ||
                                      values.courseContents?.some(
                                        (content) =>
                                          content?.resource?.type ===
                                          ResourceType.SCORM,
                                      )
                                    }
                                    inputId="isAiCustomizable"
                                    name="isAiCustomizable"
                                    label={t('content.enableAiCustomization')}
                                    value={!!values?.isAiCustomizable}
                                    onChange={async (
                                      e: InputSwitchChangeEvent,
                                    ) => {
                                      setFieldValue(
                                        'isAiCustomizable',
                                        e.value,
                                      );
                                      setTimeout(async () => {
                                        await validateForm();
                                      }, 0);
                                    }}
                                    component={FormikSwitch}
                                  />
                                </div>
                              )}

                              {account?.isSystem && (
                                <div className="field-checkbox mb-4">
                                  <Field
                                    disabled={
                                      retired ||
                                      (initialValues.customContent &&
                                        !!initialValues.id) ||
                                      values.isStandardCatalogue
                                    }
                                    inputId="customContent"
                                    name="customContent"
                                    label={t('content.custom.content')}
                                    value={!!values.customAccount}
                                    onChange={async (
                                      e: React.ChangeEvent<HTMLInputElement>,
                                    ) => {
                                      setFieldValue(
                                        'customContent',
                                        e.target.value,
                                      );
                                      if (!e.target.value) {
                                        setFieldValue('customAccount', null);
                                        setSelectedCustomAccount(null);
                                      }
                                      setTimeout(async () => {
                                        await validateForm();
                                      }, 0);
                                    }}
                                    component={FormikSwitch}
                                  />
                                </div>
                              )}
                            </FlexContainer>

                            {account?.isSystem ? (
                              <>
                                {values.customContent && (
                                  <div className="field">
                                    <AccountSelectInput
                                      isDisabled={
                                        retired ||
                                        (initialValues.customContent &&
                                          !!initialValues.id) ||
                                        values.isStandardCatalogue
                                      }
                                      defaultValue={selectedCustomAccount}
                                      onSelect={(account) => {
                                        if (account) {
                                          setFieldValue(
                                            'customAccount',
                                            account.id,
                                          );
                                          setSelectedCustomAccount(account);
                                        }
                                      }}
                                      additionalFilters={[
                                        equal('isSystem', false),
                                      ]}
                                    />
                                    <br />
                                    {!values.customAccount && (
                                      <small className="p-error">
                                        {t('course.select.account')}
                                      </small>
                                    )}
                                  </div>
                                )}

                                <label htmlFor="field-checkbox">
                                  {t('generic.type')}
                                </label>

                                <FlexContainer justify="flex-start">
                                  <div
                                    className="field-checkbox mt-2 mb-4 mr-4"
                                    style={{
                                      width: account?.isSystem
                                        ? '240px'
                                        : 'auto',
                                    }}
                                  >
                                    <Field
                                      disabled={retired || values.customContent}
                                      inputId="isStandardCatalogue"
                                      name="isStandardCatalogue"
                                      label={t('content.isStandardCatalogue')}
                                      component={FormikSwitch}
                                      value={values.isStandardCatalogue}
                                      onChange={(
                                        e: React.ChangeEvent<HTMLInputElement>,
                                      ) => {
                                        setFieldValue(
                                          'isStandardCatalogue',
                                          e.target.value,
                                        );

                                        if (!e.target.value) {
                                          setFieldValue('releaseDate', null);
                                        }
                                      }}
                                    />

                                    <AppIconTooltip
                                      target="isStandardCatalogue-icon"
                                      content={t(
                                        'course.tooltip.isStandardCatalogue',
                                      )}
                                    />
                                  </div>
                                  {!!account?.courseMinimumScore && (
                                    <div className="field-checkbox mb-4">
                                      <Field
                                        disabled={retired}
                                        inputId="exemptFromMinimumRequirementScore"
                                        name="exemptFromMinimumRequirementScore"
                                        label={t(
                                          'course.exemptFromMinimumRequirementScore',
                                        )}
                                        value={
                                          !!values.exemptFromMinimumRequirementScore
                                        }
                                        onChange={async (
                                          e: React.ChangeEvent<HTMLInputElement>,
                                        ) => {
                                          setFieldValue(
                                            'exemptFromMinimumRequirementScore',
                                            e.target.value,
                                          );
                                          setTimeout(async () => {
                                            await validateForm();
                                          }, 0);
                                        }}
                                        component={FormikSwitch}
                                      />
                                    </div>
                                  )}
                                </FlexContainer>
                                {values.isStandardCatalogue && (
                                  <CheckboxesContainer
                                    label={t(
                                      'content.standardCatalogue.settings',
                                    )}
                                  >
                                    <div className="field w-8 mb-0">
                                      <Field
                                        id="priority"
                                        label={t(
                                          'content.standardCatalogue.priority',
                                        )}
                                        name="priority"
                                        className="w-full white"
                                        component={FormikInputNumber}
                                        min={0}
                                        allowEmpty
                                        onChange={(e: InputNumberChangeEvent) =>
                                          setFieldValue(
                                            'priority',
                                            e.value,
                                            true,
                                          )
                                        }
                                        showButtons
                                      />
                                    </div>
                                    <div className="field w-8 mb-0">
                                      <Field
                                        id="label"
                                        tooltip={t(
                                          'content.standardCatalogue.label.tooltip',
                                        )}
                                        display="chip"
                                        showClear
                                        label={t(
                                          'content.standardCatalogue.label',
                                        )}
                                        name="label"
                                        filter
                                        className="w-full white"
                                        component={FormikMultiSelect}
                                        maxSelectedLabels={3}
                                        placeholder={t('generic.select')}
                                        options={standardCourseLabelOptions(
                                          t,
                                          !!values.releaseDate,
                                        )}
                                        disabled={disabled}
                                      />
                                    </div>
                                    <FlexContainer
                                      justify="flex-start"
                                      align="flex-start"
                                      gap={24}
                                    >
                                      <div className="field w-8 mb-0">
                                        <label
                                          htmlFor="course-release-date"
                                          id="release-date-label"
                                        >
                                          {t('content.releaseDate')}
                                        </label>
                                        <AppIconTooltip
                                          target="release-date-label-icon"
                                          content={t(
                                            'content.releaseDate.tooltip',
                                          )}
                                        />
                                        <AppCalendar
                                          id="course-release-date"
                                          name="releaseDate"
                                          className="w-full white-input"
                                          showIcon
                                          iconPos="left"
                                          placeholder={t('generic.select')}
                                          value={values.releaseDate}
                                          minDate={new Date()}
                                          maxDate={moment(
                                            initialValues.releaseDate,
                                          ).toDate()}
                                          onChange={(event: FormEvent) =>
                                            setFieldValue(
                                              'releaseDate',
                                              moment(event?.value?.toString())
                                                .utc(true)
                                                .toDate(),
                                            )
                                          }
                                          showButtonBar
                                          onClearButtonClick={() => {
                                            setFieldValue('releaseDate', null);
                                          }}
                                          readOnlyInput
                                          disabled={values.label?.includes(
                                            PlannerType.ONBOARDING_PLAN,
                                          )}
                                        />
                                      </div>
                                      <div>
                                        <label
                                          htmlFor="course-preview-toggle"
                                          id="course-preview-label"
                                        >
                                          {t('course.preview.enable')}
                                        </label>
                                        <AppIconTooltip
                                          target="course-preview-toggle-icon"
                                          content={t(
                                            'course.preview.enable.tooltip',
                                          )}
                                        />
                                        <div
                                          className="field-checkbox mb-0"
                                          style={{ marginTop: '22px' }}
                                        >
                                          <Field
                                            disabled={
                                              retired ||
                                              (!initialValues?.releaseDate &&
                                                initialValues.id) ||
                                              (initialValues.releaseDate &&
                                                moment(
                                                  initialValues?.releaseDate,
                                                ).isBefore(moment(), 'day'))
                                            }
                                            inputId="isPreviewable"
                                            name="isPreviewable"
                                            withText
                                            component={FormikSwitch}
                                          />
                                        </div>
                                      </div>
                                    </FlexContainer>
                                    <div>
                                      <label
                                        htmlFor="course-planner-schedule-date"
                                        id="course-planner-schedule-date-label"
                                        className={classNames({
                                          'p-error':
                                            !!errors.plannerScheduleDate,
                                        })}
                                      >
                                        {t('course.plannerScheduleDate')}
                                      </label>
                                      <AppCalendar
                                        id="course-planner-schedule-date"
                                        name="plannerScheduleDate"
                                        className="w-full white-input"
                                        showIcon
                                        iconPos="left"
                                        placeholder={t('generic.select')}
                                        value={values.plannerScheduleDate}
                                        minDate={
                                          values.releaseDate || new Date()
                                        }
                                        maxDate={moment(
                                          initialValues.plannerScheduleDate,
                                        ).toDate()}
                                        onChange={(event: FormEvent) =>
                                          setFieldValue(
                                            'plannerScheduleDate',
                                            moment(event?.value?.toString())
                                              .utc(true)
                                              .toDate(),
                                          )
                                        }
                                        showButtonBar
                                        onClearButtonClick={() => {
                                          setFieldValue(
                                            'plannerScheduleDate',
                                            null,
                                          );
                                        }}
                                        readOnlyInput
                                        disabled={values.label?.includes(
                                          PlannerType.ONBOARDING_PLAN,
                                        )}
                                      />
                                      {!!errors.plannerScheduleDate && (
                                        <small className="p-error">
                                          {errors.plannerScheduleDate}
                                        </small>
                                      )}
                                    </div>
                                  </CheckboxesContainer>
                                )}
                                <div className="field mb-4">
                                  <Field
                                    disabled={disabled || retired}
                                    id="hubspotProperty"
                                    label={t('course.hubspotProperty')}
                                    name="hubspotProperty"
                                    className="w-full"
                                    component={FormikInput}
                                    placeholder={t('course.hubspotProperty')}
                                  />
                                </div>
                                <div className="field mb-4">
                                  <Field
                                    disabled={disabled || retired}
                                    id="hubspotDateProperty"
                                    label={t('course.hubspotDateProperty')}
                                    name="hubspotDateProperty"
                                    className="w-full"
                                    component={FormikInput}
                                    placeholder={t(
                                      'course.hubspotDateProperty',
                                    )}
                                  />
                                </div>
                              </>
                            ) : (
                              <>
                                {!!account?.courseMinimumScore && (
                                  <div className="field-checkbox mb-4">
                                    <Field
                                      disabled={retired}
                                      inputId="exemptFromMinimumRequirementScore"
                                      name="exemptFromMinimumRequirementScore"
                                      label={t(
                                        'course.exemptFromMinimumRequirementScore',
                                      )}
                                      value={
                                        !!values.exemptFromMinimumRequirementScore
                                      }
                                      onChange={async (
                                        e: React.ChangeEvent<HTMLInputElement>,
                                      ) => {
                                        setFieldValue(
                                          'exemptFromMinimumRequirementScore',
                                          e.target.value,
                                        );
                                        setTimeout(async () => {
                                          await validateForm();
                                        }, 0);
                                      }}
                                      component={FormikSwitch}
                                    />
                                  </div>
                                )}
                              </>
                            )}
                          </AppFormAccordion>
                        )}
                      </div>

                      <div className="w-full">
                        <h4 className="mt-0">{t('content.builder')}</h4>
                        <div className="field mt-4 mb-0">
                          <Field
                            disabled={disabled || retired}
                            id="course-languages"
                            name="courseLanguages"
                            filter
                            label={
                              !values?.id || values?.selfHosted
                                ? t('course.files.languages')
                                : t('course.file.languages')
                            }
                            tooltip={t('content.additional.languages.tooltip')}
                            tooltipId="content-languages-info"
                            className="w-full"
                            display="chip"
                            placeholder={t('generic.select')}
                            onChange={(event: MultiSelectChangeEvent) => {
                              handleChangeCourseLanguages(
                                event,
                                values,
                                setFieldValue,
                              );
                              setTimeout(async () => {
                                await validateForm();
                              }, 0);
                            }}
                            component={FormikMultiSelect}
                            options={courseLanguagesOptions.sort((a, b) =>
                              sortAlphabetically(a.name, b.name),
                            )}
                            itemTemplate={courseLanguagesOptionTemplate}
                          />
                        </div>
                        <ContentTimeline
                          initialValues={initialValues}
                          values={values}
                          state={state}
                          disabled={disabled && !initialValues.retired}
                          setFieldValue={setFieldValue}
                          setIsSubmitDisabled={setIsSubmitDisabled}
                          pageLeaveHandler={pageLeaveHandler}
                        />
                      </div>
                    </FlexContainer>
                  </Form>
                </StyledWrapper>

                <UnsavedChangesModal
                  visible={showUnsavedChangesModal}
                  hasErrors={!!Object.keys(errors || {}).length}
                  title={t('generic.unsavedChanges')}
                  message={t('generic.unsavedChanges.message')}
                  saveButtonLabel={t('button.save')}
                  discardButtonLabel={t('button.continueWithoutSaving')}
                  onHide={() => {
                    setShowUnsavedChangesModal(false);
                  }}
                  onSave={async () => {
                    await submitFormRef?.current?.();
                    navigate(
                      pendingUrl || RedirectPaths[RedirectPathsEnum.COURSES](),
                    );
                  }}
                  onDiscard={() => {
                    setCurrentFormValues(initialValues);
                    navigate(
                      pendingUrl || RedirectPaths[RedirectPathsEnum.COURSES](),
                    );
                  }}
                />
              </FormContainer>
            )
          );
        }}
      </Formik>
    </>
  );
};
